import type {Offer} from '@/modules/hotels/components/MapView/models';
import type {ExtractPropsWithoutRef} from '@/types';

import {memo} from 'react';
import {Link} from 'react-router';

import {HotelTile as HotelTileUI} from '@/components/HotelTile';
import {HotelTilePlaceholder} from '@/components/HotelTilePlaceholder';
import {useHotelDetails, useLocalSplit} from '@/modules/common/hooks';
import {useEnvironment} from '@/modules/common/hooks/useEnvironment';
import {useLinkBuilder} from '@/modules/common/hooks/useLinkBuilder';
import {getRatingData} from '@/modules/hotels/utils/getRatingData';
import {asComponent} from '@/utils/asComponent';

type Props = Pick<
  ExtractPropsWithoutRef<typeof HotelTileUI>,
  'onClickHandleTracking'
> & {
  id: string;
  offer: Offer;
};

const HotelTileAsLink = asComponent(HotelTileUI, Link);

export const HotelTile = memo(function HotelTile({
  id,
  offer,
  onClickHandleTracking,
}: Props) {
  const {isDesktop} = useEnvironment();
  const {buildOfferListLink} = useLinkBuilder();

  const offerListLink = buildOfferListLink({
    hotelId: id,
    getSortParams: () => null,
  });
  const [data, {hotelDetails: hotelDetailsStatus}] = useHotelDetails(id);

  const priceCacheEnabled =
    useLocalSplit('FBO_2956_price_cache').treatment === 'on';

  if (hotelDetailsStatus !== 'success' || !data) {
    return <HotelTilePlaceholder />;
  }

  const {
    totalPrice,
    totalPriceInternal,
    totalPriceOriginal,
    totalDiscountDisplay,
    pricePerPerson,
    discountType,
    discountPercentage,
    totalDiscountHint,
    totalDiscountOriginal,
  } = offer;

  const rating = getRatingData({
    // @ts-expect-error ...
    hotelOffer: offer,
  });

  return (
    <HotelTileAsLink
      to={offerListLink.to}
      state={offerListLink.options.state}
      target={isDesktop ? '_blank' : '_self'}
      // ui props
      imageUrl={data.images?.[0]?.small?.url ?? ''}
      name={data.name}
      category={data.category}
      categoryType={data.categoryType}
      ratingScore={rating?.score}
      totalPrice={totalPrice}
      totalPriceInternal={totalPriceInternal}
      totalPriceOriginal={totalPriceOriginal}
      totalDiscount={totalDiscountDisplay}
      pricePerPerson={pricePerPerson}
      onClickHandleTracking={onClickHandleTracking}
      discountType={discountType}
      discountPercentage={discountPercentage}
      totalDiscountHint={totalDiscountHint}
      totalDiscountOriginal={totalDiscountOriginal}
      canShowPriceCache={priceCacheEnabled}
    />
  );
});
